import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AnnouncementsList } from '@features/announcements/components';
import { RoadmapView } from '@features/roadmap';
import { useAuthStore } from '@features/authentication/store/authStore';
import { PERMISSIONS } from '@features/shared/constants/permissions';

type HomeTab = 'announcements' | 'roadmap';

/**
 * Home is the landing surface for two pieces:
 *  - Announcements (default tab) — what's happening right now
 *  - Roadmap — where the platform is headed
 *
 * URL convention matches PicklistsTab: default tab uses no `?tab=` param,
 * the secondary tab uses `?tab=roadmap`.
 *
 * The Announcements tab is gated on `announcement.public_view` — users without
 * the permission see only the Roadmap tab (which becomes the default for them).
 */
export function HomePage() {
  usePageTitle('Home');
  const [searchParams, setSearchParams] = useSearchParams();
  const canViewPublicAnnouncements = useAuthStore((s) => s.hasPermission(PERMISSIONS.ANNOUNCEMENT_PUBLIC_VIEW));

  const requestedTab = searchParams.get('tab') === 'roadmap' ? 'roadmap' : 'announcements';
  // If the user requested announcements but lacks the perm, fall through to roadmap.
  const activeTab: HomeTab = canViewPublicAnnouncements ? requestedTab : 'roadmap';

  const handleTabChange = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === 'announcements') {
            next.delete('tab');
          } else {
            next.set('tab', value);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return (
    <div className="mx-auto max-w-4xl">
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList>
          {canViewPublicAnnouncements && (
            <TabsTrigger value="announcements">Announcements</TabsTrigger>
          )}
          <TabsTrigger value="roadmap">Roadmap</TabsTrigger>
        </TabsList>

        {canViewPublicAnnouncements && (
          <TabsContent value="announcements" className="mt-0">
            <AnnouncementsList />
          </TabsContent>
        )}

        <TabsContent value="roadmap" className="mt-0">
          <RoadmapView />
        </TabsContent>
      </Tabs>
    </div>
  );
}
